import { useMemo } from "react";
import { useLanguage } from "../i18n/LanguageContext";
import type { Language } from "../i18n/LanguageContext";
import type { NavItem, Page } from "./types";

export const ALL_CATEGORY_KEY = "All";

const PAGE_ORDER: Page[] = ["home", "sets", "partners", "contacts"];

type CatalogCopy = {
  nav: Record<Page, string>;
  languageNames: Record<Language, string>;
  layout: {
    languageLabel: string;
    footerTagline: string;
  };
  home: {
    badge: string;
    titleLead: string;
    titleEmphasis: string;
    description: string;
    searchPlaceholder: string;
    noDesserts: string;
    addButton: string;
  };
  sets: {
    badge: string;
    titleLead: string;
    titleEmphasis: string;
    description: string;
    viewSetButton: string;
  };
  partners: {
    badge: string;
    titleLead: string;
    titleEmphasis: string;
    description: string;
    locationsTitle: string;
    mapTitle: string;
    mapBadge: string;
    weSupplyTitle: string;
    ctaBadge: string;
    ctaTitleLead: string;
    ctaTitleEmphasis: string;
    ctaDescription: string;
    ctaButton: string;
  };
  contacts: {
    badge: string;
    titleLead: string;
    titleEmphasis: string;
    description: string;
    detailsTitle: string;
    addressLabel: string;
    phoneLabel: string;
    emailLabel: string;
    hoursLabel: string;
    mapTitle: string;
    watchBadge: string;
    watchLead: string;
    watchEmphasis: string;
    videoPlaceholderTitle: string;
    videoPlaceholderLead: string;
    videoPlaceholderTail: string;
    videoIframeTitle: string;
  };
  overlays: {
    quantityLabel: string;
    addToCartButton: string;
    whatsInsideLabel: string;
    setPriceLabel: string;
    pieceUnit: string;
    yourCartTitle: string;
    cartEmptyText: string;
    setBadge: string;
    removeButton: string;
    totalLabel: string;
    checkoutButton: string;
    orderIntro: string;
  };
  seo: Record<Page, { title: string; description: string }>;
};

const CATEGORY_LABELS: Record<Language, Record<string, string>> = {
  ru: {
    All: "Все",
    Cakes: "Торты",
    Tarts: "Тарты",
    Macarons: "Макаронс",
    Pastries: "Выпечка",
    Cookies: "Печенье"
  },
  kz: {
    All: "Барлығы",
    Cakes: "Торттар",
    Tarts: "Тарттар",
    Macarons: "Макарондар",
    Pastries: "Пісірмелер",
    Cookies: "Печеньелер"
  },
  en: {
    All: "All",
    Cakes: "Cakes",
    Tarts: "Tarts",
    Macarons: "Macarons",
    Pastries: "Pastries",
    Cookies: "Cookies"
  }
};

const HOURS_DAY_LABELS: Record<Language, Record<string, string>> = {
  ru: {
    Daily: "Ежедневно"
  },
  kz: {
    Daily: "Күн сайын"
  },
  en: {}
};

const CATALOG_COPY: Record<Language, CatalogCopy> = {
  ru: {
    nav: {
      home: "Главная",
      sets: "Сеты",
      partners: "Партнеры",
      contacts: "Контакты"
    },
    languageNames: {
      ru: "Рус",
      kz: "Қаз",
      en: "Eng"
    },
    layout: {
      languageLabel: "Язык",
      footerTagline: "От чистого сердца"
    },
    home: {
      badge: "",
      titleLead: "",
      titleEmphasis: "",
      description: "",
      searchPlaceholder: "Найти десерт...",
      noDesserts: "Десерты не найдены. Попробуйте другой запрос.",
      addButton: "Добавить"
    },
    sets: {
      badge: "Собрано с любовью",
      titleLead: "Готовые",
      titleEmphasis: "десертные сеты",
      description:
        "Продуманные сочетания для мероприятий, подарков или сладкого угощения для всей команды.",
      viewSetButton: "Открыть набор"
    },
    partners: {
      badge: "Наша сеть",
      titleLead: "Подаем десерты в",
      titleEmphasis: "этих замечательных местах",
      description:
        "Мы сотрудничаем с кофейнями и кафе, которые разделяют нашу любовь к качеству и ремеслу.",
      locationsTitle: "Локации",
      mapTitle: "Карта партнеров Baldai",
      mapBadge: "Где нас найти",
      weSupplyTitle: "Мы поставляем",
      ctaBadge: "Хотите сотрудничать?",
      ctaTitleLead: "Станьте",
      ctaTitleEmphasis: "партнером Baldai",
      ctaDescription: "Подавайте авторские десерты в вашем кафе. Ежедневная свежая поставка.",
      ctaButton: "Связаться с нами"
    },
    contacts: {
      badge: "",
      titleLead: "",
      titleEmphasis: "",
      description: "",
      detailsTitle: "Свяжитесь с нами",
      addressLabel: "Адрес",
      phoneLabel: "Телефон",
      emailLabel: "Почта",
      hoursLabel: "Время работы",
      mapTitle: "Локация пекарни Baldai",
      watchBadge: "Видео",
      watchLead: "Как",
      watchEmphasis: "нас найти",
      videoPlaceholderTitle: "Заглушка для видео",
      videoPlaceholderLead: "Замените ",
      videoPlaceholderTail:
        " в коде на ID вашего видео YouTube, чтобы встроить ролик о том, как добраться.",
      videoIframeTitle: "Как добраться до Baldai"
    },
    overlays: {
      quantityLabel: "Количество",
      addToCartButton: "Добавить в корзину",
      whatsInsideLabel: "Состав набора",
      setPriceLabel: "Цена набора",
      pieceUnit: "шт",
      yourCartTitle: "Корзина",
      cartEmptyText: "Ваша корзина пуста",
      setBadge: "Набор",
      removeButton: "Удалить",
      totalLabel: "Итого",
      checkoutButton: "Оформить",
      orderIntro: "Здравствуйте! Хочу оформить заказ:"
    },
    seo: {
      home: {
        title: "Baldai — авторская пекарня и десерты в Караганде",
        description:
          "Baldai — авторская пекарня в Караганде: торты, тарты, макаронс и выпечка ручной работы. Свежие десерты каждый день, доставка и самовывоз."
      },
      sets: {
        title: "Десертные сеты | Baldai",
        description:
          "Готовые десертные сеты Baldai для мероприятий, подарков и угощения команды. Продуманные сочетания вкусов от карагандинской пекарни."
      },
      partners: {
        title: "Партнеры | Baldai",
        description:
          "Кофейни и кафе Караганды, где подают десерты Baldai. Хотите продавать наши десерты у себя — станьте партнером."
      },
      contacts: {
        title: "Контакты | Baldai",
        description:
          "Адрес, телефон и время работы пекарни Baldai в Караганде: проспект Нурсултана Назарбаева, 68/1. Ежедневно с 07:00 до 18:00."
      }
    }
  },
  kz: {
    nav: {
      home: "Басты бет",
      sets: "Жиынтықтар",
      partners: "Серіктестер",
      contacts: "Байланыс"
    },
    languageNames: {
      ru: "Рус",
      kz: "Қаз",
      en: "Eng"
    },
    layout: {
      languageLabel: "Тіл",
      footerTagline: "Жүректен жүрекке"
    },
    home: {
      badge: "",
      titleLead: "",
      titleEmphasis: "",
      description: "",
      searchPlaceholder: "Десерт іздеу...",
      noDesserts: "Десерттер табылмады. Басқа сұрау енгізіп көріңіз.",
      addButton: "Қосу"
    },
    sets: {
      badge: "Махаббатпен жиналған",
      titleLead: "Дайын",
      titleEmphasis: "десерт жиынтықтары",
      description:
        "Іс-шара, сыйлық немесе командаңызға ортақ тәтті сәт үшін ойластырылған жинақтар.",
      viewSetButton: "Жиынтықты ашу"
    },
    partners: {
      badge: "Біздің желі",
      titleLead: "Десерттеріміз мына",
      titleEmphasis: "тамаша орындарда ұсынылады",
      description:
        "Сапа мен шеберлікті бағалайтын кофеханалармен және кафелермен жұмыс істейміз.",
      locationsTitle: "Мекенжайлар",
      mapTitle: "Baldai серіктестерінің картасы",
      mapBadge: "Бізді қайдан табуға болады",
      weSupplyTitle: "Біз жеткіземіз",
      ctaBadge: "Қызықтырды ма?",
      ctaTitleLead: "Baldai",
      ctaTitleEmphasis: "серіктесі болыңыз",
      ctaDescription: "Кафеңізде авторлық десерт ұсыныңыз. Күн сайын жаңа жеткізілім.",
      ctaButton: "Хабарласыңыз"
    },
    contacts: {
      badge: "",
      titleLead: "",
      titleEmphasis: "",
      description: "",
      detailsTitle: "Байланыс",
      addressLabel: "Мекенжай",
      phoneLabel: "Телефон",
      emailLabel: "Электронды пошта",
      hoursLabel: "Жұмыс уақыты",
      mapTitle: "Baldai наубайханасының орны",
      watchBadge: "Көру",
      watchLead: "Бізді қалай",
      watchEmphasis: "табуға болады",
      videoPlaceholderTitle: "Бейне орны",
      videoPlaceholderLead: "",
      videoPlaceholderTail:
        " мәнін кодта өз YouTube видео ID-іңізге ауыстырып, жету туралы видеоны осында енгізіңіз.",
      videoIframeTitle: "Baldai-ға қалай жетуге болады"
    },
    overlays: {
      quantityLabel: "Саны",
      addToCartButton: "Себетке қосу",
      whatsInsideLabel: "Құрамы",
      setPriceLabel: "Жиынтық бағасы",
      pieceUnit: "дана",
      yourCartTitle: "Себет",
      cartEmptyText: "Себет бос",
      setBadge: "Жиынтық",
      removeButton: "Өшіру",
      totalLabel: "Жалпы",
      checkoutButton: "Рәсімдеу",
      orderIntro: "Сәлеметсіз бе! Тапсырыс бергім келеді:"
    },
    seo: {
      home: {
        title: "Baldai — Қарағандыдағы авторлық наубайхана және десерттер",
        description:
          "Baldai — Қарағандыдағы авторлық наубайхана: торттар, тарттар, макарондар және қолдан жасалған пісірмелер. Күн сайын жаңа десерттер, жеткізу және өзі алып кету."
      },
      sets: {
        title: "Десерт жиынтықтары | Baldai",
        description:
          "Іс-шаралар, сыйлықтар және команда үшін Baldai десерт жиынтықтары. Қарағанды наубайханасының ойластырылған дәм үйлесімдері."
      },
      partners: {
        title: "Серіктестер | Baldai",
        description:
          "Baldai десерттері ұсынылатын Қарағанды кофеханалары мен кафелері. Десерттерімізді өзіңізде сатқыңыз келсе — серіктес болыңыз."
      },
      contacts: {
        title: "Байланыс | Baldai",
        description:
          "Baldai наубайханасының мекенжайы, телефоны және жұмыс уақыты: Нұрсұлтан Назарбаев даңғылы, 68/1, Қараганды. Күн сайын 07:00-ден 18:00-ге дейін."
      }
    }
  },
  en: {
    nav: {
      home: "Home",
      sets: "Sets",
      partners: "Partners",
      contacts: "Contacts"
    },
    languageNames: {
      ru: "Rus",
      kz: "Kaz",
      en: "Eng"
    },
    layout: {
      languageLabel: "Language",
      footerTagline: "From our heart to yours"
    },
    home: {
      badge: "",
      titleLead: "",
      titleEmphasis: "",
      description: "",
      searchPlaceholder: "Search our desserts...",
      noDesserts: "No desserts found. Try another search.",
      addButton: "Add"
    },
    sets: {
      badge: "Bundled with love",
      titleLead: "Curated",
      titleEmphasis: "dessert sets",
      description:
        "Thoughtfully assembled combinations — perfect for events, gifting, or simply treating your whole team.",
      viewSetButton: "View Set"
    },
    partners: {
      badge: "Our Network",
      titleLead: "Proudly served at",
      titleEmphasis: "these wonderful places",
      description:
        "We partner with cafés and coffee shops that share our passion for quality and craft. Find our desserts fresh on their menus.",
      locationsTitle: "Locations",
      mapTitle: "Baldai partners map",
      mapBadge: "Where to find us",
      weSupplyTitle: "We supply",
      ctaBadge: "Interested?",
      ctaTitleLead: "Become a",
      ctaTitleEmphasis: "Baldai partner",
      ctaDescription: "Serve handcrafted patisserie in your café. Delivered fresh, daily.",
      ctaButton: "Get in Touch"
    },
    contacts: {
      badge: "",
      titleLead: "",
      titleEmphasis: "",
      description: "",
      detailsTitle: "Get in touch",
      addressLabel: "Address",
      phoneLabel: "Phone",
      emailLabel: "Email",
      hoursLabel: "Hours",
      mapTitle: "Baldai Bakery location",
      watchBadge: "Watch",
      watchLead: "How to",
      watchEmphasis: "find us",
      videoPlaceholderTitle: "Video placeholder",
      videoPlaceholderLead: "Replace ",
      videoPlaceholderTail:
        " in the code with your real YouTube video ID to embed your \"how to arrive\" video here.",
      videoIframeTitle: "How to arrive at Baldai"
    },
    overlays: {
      quantityLabel: "Quantity",
      addToCartButton: "Add to Cart",
      whatsInsideLabel: "What's inside",
      setPriceLabel: "Set price",
      pieceUnit: "pcs",
      yourCartTitle: "Your Cart",
      cartEmptyText: "Your cart is empty",
      setBadge: "Set",
      removeButton: "Remove",
      totalLabel: "Total",
      checkoutButton: "Checkout",
      orderIntro: "Hello! I'd like to place an order:"
    },
    seo: {
      home: {
        title: "Baldai — Handcrafted Bakery & Desserts in Karaganda",
        description:
          "Baldai is a handcrafted bakery in Karaganda serving cakes, tarts, macarons and pastries. Fresh desserts daily, with delivery and pickup."
      },
      sets: {
        title: "Dessert Sets | Baldai",
        description:
          "Curated Baldai dessert sets for events, gifting, or treating your whole team — thoughtfully assembled by our Karaganda bakery."
      },
      partners: {
        title: "Partners | Baldai",
        description:
          "The cafés and coffee shops in Karaganda that serve Baldai desserts. Want to carry our desserts too? Become a partner."
      },
      contacts: {
        title: "Contact Us | Baldai",
        description:
          "Baldai bakery's address, phone, and hours in Karaganda: Nursultan Nazarbayev Avenue 68/1. Open daily 07:00–18:00."
      }
    }
  }
};

export function useCatalogI18n() {
  const { language, setLanguage, languages } = useLanguage();
  const copy = CATALOG_COPY[language];

  const navItems = useMemo<NavItem[]>(
    () =>
      PAGE_ORDER.map((page) => ({
        key: page,
        label: copy.nav[page]
      })),
    [copy.nav]
  );

  const localizeCategory = (category: string): string =>
    CATEGORY_LABELS[language][category] ?? category;

  const localizeHoursDay = (days: string): string => HOURS_DAY_LABELS[language][days] ?? days;

  const localizeSetSummary = (itemCount: number): string => {
    if (language === "ru") {
      return `${itemCount} позиций`;
    }
    if (language === "kz") {
      return `${itemCount} өнім`;
    }
    return `${itemCount} items`;
  };

  return {
    language,
    setLanguage,
    languages,
    copy,
    navItems,
    localizeCategory,
    localizeHoursDay,
    localizeSetSummary
  };
}
